/**
 * Outbound Calls API Service
 *
 * Handles API calls for outbound appointment confirmation calls.
 */

import { apiFetch } from './apiClient';
import log from 'loglevel';

const BASE_PATH = '/outbound-calls';

/**
 * Initiate an outbound confirmation call for an appointment.
 *
 * @param {string} appointmentId - UUID of the appointment
 * @param {Array<{start: string, end: string}>} alternativeTimes - Optional alternative time slots
 * @param {string} language - Optional language code (e.g., "en", "es", "fr")
 * @returns {Promise<Object>} The created outbound call record
 */
export const initiateOutboundCall = async (appointmentId, alternativeTimes = null, language = null) => {
  try {
    const body = { appointment_id: appointmentId };
    if (alternativeTimes && alternativeTimes.length > 0) {
      body.alternative_times = alternativeTimes;
    }
    if (language) {
      body.language = language;
    }

    const response = await apiFetch(`${BASE_PATH}/initiate`, {
      method: 'POST',
      body: JSON.stringify(body),
    });

    console.log('[outboundCalls.js] initiateOutboundCall raw response:', response);
    console.log('[outboundCalls.js] initiateOutboundCall response.data:', response.data);
    return response.data; // Unwrap from { data: {...}, message: "..." }
  } catch (error) {
    log.error('Failed to initiate outbound call:', error);
    throw error;
  }
};

/**
 * List outbound calls for the user's clinic.
 *
 * @param {Object} options - Query options
 * @param {string} options.status - Filter by status (PENDING, IN_PROGRESS, COMPLETED, FAILED)
 * @param {string} options.outcome - Filter by outcome
 * @param {number} options.limit - Max results (default 50)
 * @param {number} options.offset - Pagination offset
 * @returns {Promise<{calls: Array, total: number, limit: number, offset: number}>}
 */
export const listOutboundCalls = async ({ status, outcome, limit = 50, offset = 0 } = {}) => {
  try {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (outcome) params.append('outcome', outcome);
    params.append('limit', limit.toString());
    params.append('offset', offset.toString());

    const response = await apiFetch(`${BASE_PATH}?${params.toString()}`);
    return response.data; // Unwrap from { data: {...}, message: "..." }
  } catch (error) {
    log.error('Failed to list outbound calls:', error);
    throw error;
  }
};

/**
 * Get details of a specific outbound call.
 *
 * @param {string} callId - UUID of the outbound call
 * @param {boolean} includeTranscript - Whether to include the full transcript
 * @returns {Promise<Object>} Outbound call details
 */
export const getOutboundCallDetails = async (callId, includeTranscript = false) => {
  try {
    const params = new URLSearchParams();
    if (includeTranscript) params.append('include_transcript', 'true');

    const response = await apiFetch(`${BASE_PATH}/${callId}?${params.toString()}`);
    console.log('[outboundCalls.js] getOutboundCallDetails raw response:', response);
    console.log('[outboundCalls.js] getOutboundCallDetails response.data:', response.data);
    return response.data; // Unwrap from { data: {...}, message: "..." }
  } catch (error) {
    log.error('Failed to get outbound call details:', error);
    throw error;
  }
};

/**
 * Override the outcome of an outbound call.
 *
 * @param {string} callId - UUID of the outbound call
 * @param {string} outcome - New outcome value
 * @param {string} notes - Optional notes about the override
 * @returns {Promise<Object>} Updated outcome record
 */
export const overrideOutboundCallOutcome = async (callId, outcome, notes = null) => {
  try {
    const body = { outcome };
    if (notes) body.notes = notes;

    const response = await apiFetch(`${BASE_PATH}/${callId}/outcome`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });

    return response.data; // Unwrap from { data: {...}, message: "..." }
  } catch (error) {
    log.error('Failed to override outbound call outcome:', error);
    throw error;
  }
};

/**
 * Get audio URL for an outbound call recording.
 *
 * @param {string} callId - UUID of the outbound call
 * @returns {Promise<{audio_url: string, conversation_id: string, content_type: string}>}
 */
export const fetchOutboundCallAudio = async (callId) => {
  try {
    const response = await apiFetch(`${BASE_PATH}/${callId}/audio`);
    return response;
  } catch (error) {
    log.error('Failed to fetch outbound call audio:', error);
    throw error;
  }
};

/**
 * Outcome type constants
 */
export const OutboundCallOutcome = {
  CONFIRMED: 'CONFIRMED',
  CONFIRMED_ORIGINAL: 'CONFIRMED_ORIGINAL',
  SELECTED_ALTERNATIVE_1: 'SELECTED_ALTERNATIVE_1',
  SELECTED_ALTERNATIVE_2: 'SELECTED_ALTERNATIVE_2',
  SELECTED_ALTERNATIVE_3: 'SELECTED_ALTERNATIVE_3',
  REJECTED_ALL_OPTIONS: 'REJECTED_ALL_OPTIONS',
  NEEDS_RESCHEDULE: 'NEEDS_RESCHEDULE',
  NO_ANSWER: 'NO_ANSWER',
  FAILED: 'FAILED',
  INCOMPLETE: 'INCOMPLETE',
};

/**
 * Status type constants
 */
export const OutboundCallStatus = {
  PENDING: 'PENDING',
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
};

/**
 * Get display label for an outcome - clear, actionable labels
 */
export const getOutcomeLabel = (outcome) => {
  const labels = {
    [OutboundCallOutcome.CONFIRMED]: 'Confirmed',
    [OutboundCallOutcome.CONFIRMED_ORIGINAL]: 'Confirmed',
    [OutboundCallOutcome.SELECTED_ALTERNATIVE_1]: 'Rescheduled',
    [OutboundCallOutcome.SELECTED_ALTERNATIVE_2]: 'Rescheduled',
    [OutboundCallOutcome.SELECTED_ALTERNATIVE_3]: 'Rescheduled',
    [OutboundCallOutcome.REJECTED_ALL_OPTIONS]: 'Declined',
    [OutboundCallOutcome.NEEDS_RESCHEDULE]: 'Needs Reschedule',
    [OutboundCallOutcome.NO_ANSWER]: 'No Answer',
    [OutboundCallOutcome.FAILED]: 'Call Failed',
    [OutboundCallOutcome.INCOMPLETE]: 'Incomplete',
  };
  return labels[outcome] || outcome;
};

/**
 * Check if outcome indicates patient accepted a suggestion
 */
export const isAcceptedOutcome = (outcome) => {
  return [
    OutboundCallOutcome.CONFIRMED,
    OutboundCallOutcome.CONFIRMED_ORIGINAL,
    OutboundCallOutcome.SELECTED_ALTERNATIVE_1,
    OutboundCallOutcome.SELECTED_ALTERNATIVE_2,
    OutboundCallOutcome.SELECTED_ALTERNATIVE_3,
  ].includes(outcome);
};

/**
 * Check if patient accepted an alternative time suggestion
 */
export const isAlternativeAccepted = (outcome) => {
  return [
    OutboundCallOutcome.SELECTED_ALTERNATIVE_1,
    OutboundCallOutcome.SELECTED_ALTERNATIVE_2,
    OutboundCallOutcome.SELECTED_ALTERNATIVE_3,
  ].includes(outcome);
};

/**
 * Get the alternative number if accepted (1, 2, or 3), or null
 */
export const getAcceptedAlternativeNumber = (outcome) => {
  switch (outcome) {
    case OutboundCallOutcome.SELECTED_ALTERNATIVE_1:
      return 1;
    case OutboundCallOutcome.SELECTED_ALTERNATIVE_2:
      return 2;
    case OutboundCallOutcome.SELECTED_ALTERNATIVE_3:
      return 3;
    default:
      return null;
  }
};

/**
 * Get color classes for an outcome badge
 * Modern, subtle styling with soft shadows and refined colors
 */
export const getOutcomeColorClasses = (outcome) => {
  const colors = {
    [OutboundCallOutcome.CONFIRMED]: 'bg-gradient-to-r from-emerald-50 to-green-50 text-emerald-700 ring-1 ring-emerald-500/20 shadow-sm shadow-emerald-100',
    [OutboundCallOutcome.CONFIRMED_ORIGINAL]: 'bg-gradient-to-r from-emerald-50 to-green-50 text-emerald-700 ring-1 ring-emerald-500/20 shadow-sm shadow-emerald-100',
    [OutboundCallOutcome.SELECTED_ALTERNATIVE_1]: 'bg-gradient-to-r from-sky-50 to-blue-50 text-sky-700 ring-1 ring-sky-500/20 shadow-sm shadow-sky-100',
    [OutboundCallOutcome.SELECTED_ALTERNATIVE_2]: 'bg-gradient-to-r from-sky-50 to-blue-50 text-sky-700 ring-1 ring-sky-500/20 shadow-sm shadow-sky-100',
    [OutboundCallOutcome.SELECTED_ALTERNATIVE_3]: 'bg-gradient-to-r from-sky-50 to-blue-50 text-sky-700 ring-1 ring-sky-500/20 shadow-sm shadow-sky-100',
    [OutboundCallOutcome.REJECTED_ALL_OPTIONS]: 'bg-gradient-to-r from-red-50 to-rose-50 text-red-700 ring-1 ring-red-500/20 shadow-sm shadow-red-100',
    [OutboundCallOutcome.NEEDS_RESCHEDULE]: 'bg-gradient-to-r from-orange-50 to-amber-50 text-orange-700 ring-1 ring-orange-500/20 shadow-sm shadow-orange-100',
    [OutboundCallOutcome.NO_ANSWER]: 'bg-gradient-to-r from-slate-50 to-gray-100 text-slate-600 ring-1 ring-slate-400/20 shadow-sm shadow-slate-100',
    [OutboundCallOutcome.FAILED]: 'bg-gradient-to-r from-red-50 to-rose-50 text-red-700 ring-1 ring-red-500/20 shadow-sm shadow-red-100',
    [OutboundCallOutcome.INCOMPLETE]: 'bg-gradient-to-r from-gray-50 to-slate-50 text-gray-500 ring-1 ring-gray-400/20 shadow-sm shadow-gray-100',
  };
  return colors[outcome] || 'bg-gradient-to-r from-gray-50 to-slate-50 text-gray-500 ring-1 ring-gray-400/20 shadow-sm';
};

/**
 * Check if outcome needs attention/action from staff
 */
export const needsAttention = (outcome) => {
  return [
    OutboundCallOutcome.NO_ANSWER,
    OutboundCallOutcome.NEEDS_RESCHEDULE,
    OutboundCallOutcome.REJECTED_ALL_OPTIONS,
    OutboundCallOutcome.FAILED,
    OutboundCallOutcome.INCOMPLETE,
  ].includes(outcome);
};

/**
 * Get status label
 */
export const getStatusLabel = (status) => {
  const labels = {
    [OutboundCallStatus.PENDING]: 'Pending',
    [OutboundCallStatus.IN_PROGRESS]: 'In Progress',
    [OutboundCallStatus.COMPLETED]: 'Completed',
    [OutboundCallStatus.FAILED]: 'Failed',
  };
  return labels[status] || status;
};

/**
 * Get status color classes
 */
export const getStatusColorClasses = (status) => {
  const colors = {
    [OutboundCallStatus.PENDING]: 'bg-amber-100 text-amber-800',
    [OutboundCallStatus.IN_PROGRESS]: 'bg-blue-100 text-blue-800',
    [OutboundCallStatus.COMPLETED]: 'bg-green-100 text-green-800',
    [OutboundCallStatus.FAILED]: 'bg-red-100 text-red-800',
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
};
